import { ActionTree } from "vuex";
import { Mobile, Guard, MobileGuardsRequest } from "./types";
import { RootState } from "../types";
import api from "@/services/api.mobile.service";

export const actions: ActionTree<Mobile, RootState> = {
	async registerUserDevice(_, { deviceId, deviceType }) {
		return await api.registerUserDevice(deviceId, deviceType);
	},

	async setLocationForGuard(_, location: string) {
		await api.setLocationForGuard(location);
	},

	async mobileGuardLogout({ state }, { deviceId, deviceType }) {
		await api.mobileGuardLogout(state.DispatchGuard.guardId, deviceId, deviceType);
	},

	async extendMobileSession(_) {
		await api.extendMobileSession();
	},

	async loadCallSigns({ commit }) {
		const callsigns = await api.loadCallSigns();
		commit("setCallSigns", callsigns || []);
	},

	async loadCoverCallSigns({ commit }): Promise<void> {
		const { data } = await api.loadCoverCallSigns();
		commit("setCoverCallSigns", data ?? []);
	},

	async loadDispatchRequestsForGuard({ commit }, guardId: number) {
		const dispatchRequests = await api.loadDispatchRequestsForGuard(
			guardId
		);
		commit("setDispatchRequests", dispatchRequests || []);
		commit("setGuardId", guardId);
	},

	async claimDispatchRequest({ commit, state }, dispatchQueueId) {
		await api.claimDispatchRequest(dispatchQueueId, state.DispatchGuard.guardId);
		commit("claimDispatchRequest", dispatchQueueId);
	},

	async unclaimDispatchRequest({ commit, state }, dispatchQueueId) {
		await api.unclaimDispatchRequest(dispatchQueueId, state.DispatchGuard.guardId);
		commit("unclaimDispatchRequest", dispatchQueueId);
	},

	async activateDispatchRequest({ commit, state }, dispatchQueueId) {
		await api.activateDispatchRequest(dispatchQueueId, state.DispatchGuard.guardId);
		commit("activateDispatchRequest", dispatchQueueId);
	},

	async inactivateDispatchRequest({ commit, state }, dispatchQueueId) {
		await api.inactivateDispatchRequest(dispatchQueueId, state.DispatchGuard.guardId);
		commit("inactivateDispatchRequest", dispatchQueueId);
	},

	async guardArrived({ commit, state }, { dispatchQueueId, hasArrived }) {
		if (hasArrived) {
			await api.guardArrived(dispatchQueueId, state.DispatchGuard.guardId);
		}
		commit("setHasArrived", hasArrived);
	},

	async guardAvailable({ commit, state }) {
		await api.guardAvailable(state.DispatchGuard.guardId);
		commit("setGuardStatus", "Available");
	},

	async finishDispatchRequest({ state }, dispatchQueueId) {
		await api.finishDispatchRequest(dispatchQueueId, state.DispatchGuard.guardId);
	},

	async continueTour({ state }, { tourId, stepId, dispatchQueueId }) {
		await api.continueTour(tourId, stepId, dispatchQueueId, state.DispatchGuard.guardId);
	},

	async addDispatchRequest(
		_,
		{
			preferredGuardId,
			patrolZoneId,
			eventId,
			requester,
			requesterDescription,
			location,
			siteTitle
		}
	) {
		await api.addDispatchRequest(
			preferredGuardId,
			patrolZoneId,
			eventId,
			requester,
			requesterDescription,
			location,
			siteTitle
		);
	},

	async getGuardTourByEvent({ commit }, eventId: number) {
		const res = await api.getGuardTourByEvent(eventId);
		if (res.status == 200) {
			commit("setCurrentTour", res.data);
		}
	},

	async fetchAuditEndpoint({ commit }) {
		const endpoint = await api.fetchAuditEndpoint();
		commit("setAuditEndpoint", endpoint);
	},

	async addNoteForEvent({ state }, { guardNote, eventId }) {
		await api.addNoteForEvent(state.DispatchGuard.guardId, guardNote, eventId);
	},

	async setGuardForUser({ commit }, guardId): Promise<void> {
		const callsign = await api.setGuardForUser(guardId);
		commit("setCallSign", callsign);
		commit("setGuardId", guardId);
		commit("setGuardStatusEnum", 1);
	},

	async loadGuardForUser({ commit }): Promise<void> {
		const guard = await api.loadGuardForUser();
		if (guard) {
			commit("setCallSign", guard.callsign);
			commit("setGuardId", guard.guardID);
			commit("setOriginalCallSign", guard.originalCallSign);
			commit("setGuardStatusEnum", guard.status);
		}
	},

	async setUnavailableReason(
		{ commit, state },
		{ unavailableStatusId, unavailableReason }
	) {
		await api.setUnavailableStatus(
			state.DispatchGuard.guardId,
			unavailableStatusId,
			unavailableReason
		);
		commit("setUnavailableReason", unavailableReason);
		commit("setUnavailableStatus", unavailableStatusId);
		commit("setGuardStatus", "Unavailable");
	},

	async requestCoverGuard({ commit, dispatch }, coverGuardId: number) : Promise<void> {
		await api.requestCover(coverGuardId);
		commit("setRequestCover", true);
		await dispatch("loadGuardForUser");
	},

	async loadCoverRequest({ commit }): Promise<void> {
		const { data } = await api.loadCoverRequest();
		commit("setCoverRequests", data);
	},

	async rejectCover({ commit }, breakGuardId: number): Promise<void> {
		await api.rejectCover(breakGuardId);
		commit("setCoverRequests", null);
	},

	async acceptCover({ commit }, reliefGuardId): Promise<void> {
		await api.acceptCover(reliefGuardId);
		commit("setCoverRequests", null);
	},

	async endCover({ dispatch }): Promise<void> {
		const { data } = await api.endCover();

		if (data.guardID) {
			// Set this user to be original guard's id
			await dispatch("setGuardForUser", data.guardID);
			await dispatch("loadGuardForUser");

			// Reloads the dispatch queue with the correct information
			await dispatch("loadDispatchRequestsForGuard", data.guardID);

			// Should clear cover requests
			await dispatch("loadCoverRequests");
		}
	},

	async cancelCover({ commit, dispatch }): Promise<void> {
		await api.cancelCover();
		commit("setRequestCover", false);
		await dispatch("loadGuardForUser");
	},

	async loadNotesForEvent({ commit }, eventId) {
		const notes = await api.loadNotesForEvent(eventId);
		commit("setNotesForEvent", notes);
	},

	async loadMobileGuardsForEvent({ commit }, mobileGuardsRequest: MobileGuardsRequest) {
		const mobileGuards: Guard[] = await api.loadMobileGuardsForEvent(mobileGuardsRequest);
		commit("setMobileGuardsForEvent", mobileGuards);
	},

	async loadUnavailableStatuses({ commit }) {
		const unavailableStatuses = await api.loadUnavailableStatuses();
		commit("setGuardUnavailableStatuses", unavailableStatuses);
	},

	async addEventRecordForGuard(
		_,
		{ eventId, details, objectId, eventRecordTypeId }
	) {
		return await api.addEventRecordForGuard(
			eventId,
			details,
			objectId,
			eventRecordTypeId
		);
	},

	async loadGuardStatus({ commit, state }) {
		if (typeof state.DispatchGuard.guardId !== undefined && state.DispatchGuard.guardId > 0) {
			const status = await api.loadGuardStatus(state.DispatchGuard.guardId);
			commit("setGuardStatus", status);
		}
	},

	async loadStarredDevices({ commit }) {
		const devices = await api.getAllStarredDevices();
		commit("setStarredDevices", devices);
	},

	async toggleDeviceStarred({ dispatch }, deviceId) {
		await api.toggleDeviceStarred(deviceId);
		await dispatch("loadStarredDevices");
	},

	async loadQuickCloseOutcomes({ commit }) {
		const outcomes = await api.getQuickCloseOutcomes();
		commit("setQuickCloseList", outcomes);
	},

	async loadPatrolZones({ commit }) {
		const patrolZones = await api.loadPatrolZones();
		commit("setPatrolZones", patrolZones);
	},

	async loadCurrentTourStepIdentifier({ commit }, tourStepId: number) {
		const locationIdentifier = await api.getTourStepLocationId(tourStepId);

		if (locationIdentifier) {
			commit("setCurrentTourStepIdentifier", locationIdentifier);
		}
	},

	/**
	 * Promotes the current guard to the incident commander of a dispatch request.
	 * @param param0 vuex store
	 * @param dispatchQueueId the dispatch request id
	 */
	async promoteSelfToIncidentCommander({ dispatch, getters }, dispatchQueueId: number) {
		const guardId = getters.getGuardId;

		if (guardId) {
			await api.assignIncidentCommander(guardId, dispatchQueueId);
			await dispatch("loadDispatchRequestsForGuard", guardId);
		}
	},

	async loadEventIdsForUser({ commit })
	{
		var eventIds = await api.getEventIdsForUser();
		commit("setUserSharedEventIds", eventIds);
	}
};
